import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faDownload,
    faImage,
    faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";

import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    serverTimestamp,
    setDoc,
    where,
} from "firebase/firestore";

import { toast } from "react-toastify";

import AnnotateModal from "../../AnnotateModal/AnnotateModal";

const PDF_PHOTO_SLOTS = [
    { key: "front", label: "Front", field: "doorPullPhotoLink" },
    { key: "back", label: "Back", field: "doorPushPhotoLink" },
    { key: "hinge", label: "Hinge", field: "hingePhotoLink" },
    { key: "cert", label: "Cert", field: "doorCertPhotoLink" },
    { key: "lock", label: "Lock", field: "lockPhotoLink" },
    { key: "letterbox", label: "Letterbox", field: "letterBoxPhotoLink" },
    { key: "damage", label: "Damage", field: "doorDamageLink" },
];


const DOOR_PART_CLASSES = [
    "bolt_latch",
    "code_lock",
    "door",
    "door_bell",
    "door_closer",
    "door_damage",
    "door_handle",
    "door_knob",
    "door_latch ",
    "door_number",
    "drop_down_seal",
    "emergency_lock",
    "external_lock",
    "fire_exit",
    "frame_damage",
    "glass",
    "hinge",
    "keep_clear",
    "keep_locked",
    "keep_shut",
    "keyless_exit",
    "knocker",
    "letter_box",
    "mortice_cover",
    "mortice_lock",
    "night_latch",
    "padlock",
    "pull_handle",
    "push_pad",
    "safety_glass",
    "signage",
    "spyhole",
    "ventilation",
    "yale_lock",
];

const HINGE_CLASSES = [
    "loose",
    "missing",
    "normal",
    "reflection",
];

const DESTINATION_ANNOTATION_FIELDS = {
    doorPullPhotoLink: "pullAiAnnotations",
    doorPushPhotoLink: "pushAiAnnotations",
    hingePhotoLink: "hingeAiAnnotations",
    // doorCertPhotoLink: "certAiAnnotations",
    // lockPhotoLink: "lockAiAnnotations",
    // letterBoxPhotoLink: "letterBoxAiAnnotations",
    // doorDamageLink: "damageAiAnnotations",
};

const ALL_SOURCE_IMAGE_FIELDS = [
    "doorPullPhotoLink",
    "doorPushPhotoLink",
    "doorCertPhotoLink",
    "hingePhotoLink",
    "hingePhotoLink2",
    "hingePhotoLink3",
    "lockPhotoLink",
    "lockPhotoLink2",
    "lockPhotoLink3",
    "letterBoxPhotoLink",
    "doorDamageLink",
    "doorDamageLink2",
    "doorDamageLink3",
    "doorDamageLink4",
    "doorDamageLink5",
    "glassFireStampLink",
    "cupboard_contense_photo",
    "workDonePhoto",
];

const inferSourceFieldFromTitle = (title = "") => {
    const value = String(title).trim().toLowerCase();

    if (value === "pull" || value === "front") {
        return "doorPullPhotoLink";
    }

    if (value === "push" || value === "back") {
        return "doorPushPhotoLink";
    }

    if (
        value === "cert" ||
        value.includes("certification") ||
        value.includes("certificate")
    ) {
        return "doorCertPhotoLink";
    }

    if (value.includes("hinge 3")) {
        return "hingePhotoLink3";
    }

    if (value.includes("hinge 2")) {
        return "hingePhotoLink2";
    }

    if (value.includes("hinge")) {
        return "hingePhotoLink";
    }

    if (value.includes("lock 3")) {
        return "lockPhotoLink3";
    }

    if (value.includes("lock 2")) {
        return "lockPhotoLink2";
    }

    if (value.includes("lock")) {
        return "lockPhotoLink";
    }

    if (value.includes("letter")) {
        return "letterBoxPhotoLink";
    }

    if (value.includes("damage 5")) {
        return "doorDamageLink5";
    }

    if (value.includes("damage 4")) {
        return "doorDamageLink4";
    }

    if (value.includes("damage 3")) {
        return "doorDamageLink3";
    }

    if (value.includes("damage 2")) {
        return "doorDamageLink2";
    }

    if (value.includes("damage")) {
        return "doorDamageLink";
    }

    if (value.includes("fire stamp")) {
        return "glassFireStampLink";
    }

    if (value.includes("cupboard")) {
        return "cupboard_contense_photo";
    }

    if (value.includes("work done")) {
        return "workDonePhoto";
    }

    return null;
};

const useWindowWidth = () => {
    const getWidth = () =>
        typeof window !== "undefined" ? window.innerWidth : 1200;

    const [width, setWidth] = useState(getWidth);

    useEffect(() => {
        const handleResize = () => setWidth(getWidth());

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return width;
};

const HoverButton = ({
    children,
    onClick,
    disabled = false,
    title,
    style = {},
    hoverStyle = {},
    activeStyle = {},
    isActive = false,
    onMouseEnter,
    onMouseLeave,
    ...rest
}) => {
    const [hovered, setHovered] = useState(false);

    return (
        <button
            {...rest}
            type="button"
            title={title}
            disabled={disabled}
            onClick={onClick}
            onMouseEnter={(event) => {
                setHovered(true);
                onMouseEnter?.(event);
            }}
            onMouseLeave={(event) => {
                setHovered(false);
                onMouseLeave?.(event);
            }}
            style={{
                ...style,
                ...(isActive ? activeStyle : {}),
                ...(hovered && !disabled ? hoverStyle : {}),
            }}
        >
            {children}
        </button>
    );
};

const CircleCloseButton = ({
    onClick,
    title = "Close",
    size = 36,
}) => {
    const [hovered, setHovered] = useState(false);

    return (
        <button
            type="button"
            title={title}
            aria-label={title}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: `${size}px`,
                height: `${size}px`,
                flex: `0 0 ${size}px`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                paddingBottom: "2px",
                border: `1px solid ${hovered ? "#dc2626" : "#d4d4d8"}`,
                borderRadius: "50%",
                background: hovered ? "#dc2626" : "#fff",
                color: hovered ? "#fff" : "#18181b",
                fontSize: size >= 34 ? "23px" : "17px",
                fontWeight: 300,
                lineHeight: 1,
                cursor: "pointer",
                transition:
                    "background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease",
            }}
        >
            ×
        </button>
    );
};

const PhotoSelector = ({
    show,
    report,
    doorHistory = [],
    currentReportUid = null,
    currentDoorId = null,

    db,
    buildingData = null,

    classColors = {
        door: "#f44336",
        glass: "#00bcd4",
        safety_glass: "#0097a7",
        letter_box: "#ffeb3b",
        knocker: "#8d6e63",
        spyhole: "#9c27b0",
        door_bell: "#fbc02d",
        hinge: "#009688",
        door_closer: "#4caf50",
        fire_exit: "#2e7d32",
        keep_shut: "#ef5350",
        signage: "#ffca28",
        door_damage: "#d32f2f",
        frame_damage: "#e91e63",
        door_handle: "#fb8c00",
        night_latch: "#ff9800",
        yale_lock: "#795548",
        mortice_lock: "#3f51b5",
        keyless_exit: "#00bcd4",
        code_lock: "#2196f3",
        bolt_latch: "#ff7043",
        mortice_cover: "#7b1fa2",
        emergency_lock: "#9c27b0",
        padlock: "#607d8b",
        push_pad: "#ff5722",
        door_knob: "#fb8c00",
        ventilation: "#90a4ae",
        keep_clear: "#26c6da",
        external_lock: "#ff6f00",
        door_latch: "#558b2f",

        normal: "#4caf50",
        missing: "#f44336",
        reflection: "#ff9800",
        loose: "#66bb6a",

        fd30_sticker: "#ab47bc",
        fd30_stamp: "#8e24aa",
        fd60_sticker: "#6a1b9a",
        fd60_stamp: "#4a148c"
    },
    onClose,
    onGenerate,
}) => {
    const windowWidth = useWindowWidth();

    const isTablet = windowWidth <= 900;
    const isMobile = windowWidth <= 560;

    const [photoCandidates, setPhotoCandidates] = useState([]);
    const [selectedPhotos, setSelectedPhotos] = useState({});
    const [selectedPhotoIds, setSelectedPhotoIds] = useState({});
    const [selectedPhotoSources, setSelectedPhotoSources] = useState({});
    const [defaultPhotos, setDefaultPhotos] = useState({});
    const [defaultPhotoIds, setDefaultPhotoIds] = useState({});
    const [defaultPhotoSources, setDefaultPhotoSources] = useState({});
    const [activeSlot, setActiveSlot] = useState("front");
    const [selectedDate, setSelectedDate] = useState("all");
    const [generating, setGenerating] = useState(false);
    const [hoveredSlot, setHoveredSlot] = useState(null);
    const [hoveredCard, setHoveredCard] = useState(null);

    const [showManualAnnotateModal, setShowManualAnnotateModal] =
        useState(false);

    const [showAnnotateModalResult, setShowAnnotateModalResult] =
        useState(null);

    const [currentAnnotatingDoor, setCurrentAnnotatingDoor] =
        useState(null);

    const [savingAnnotation, setSavingAnnotation] =
        useState(false);

    const [loadingAnnotation, setLoadingAnnotation] =
        useState(false);

    const [annotationLoadError, setAnnotationLoadError] =
        useState(null);

    const [annotationLoadingInfo, setAnnotationLoadingInfo] =
        useState(null);

    const annotationLoadRequestRef = useRef(0);

    const currentDoor = Array.isArray(report) ? report?.[0] : report;

    const historyDoorId =
        (doorHistory || [])
            .map((entry) =>
                entry?.sourceDoorId ||
                entry?._doorId ||
                entry?.doorId ||
                null,
            )
            .find(Boolean) ||
        null;

    const currentBuildingReportUid =
        buildingData?.uid ||
        buildingData?.reportId ||
        currentDoor?.buildingId ||
        currentDoor?.reportUid ||
        null;

    const preferredSourceReportUid =
        currentReportUid ||
        null;

    const effectiveCurrentReportUid =
        preferredSourceReportUid ||
        currentBuildingReportUid;

    const effectiveCurrentDoorId =
        currentDoorId ||
        currentDoor?._doorId ||
        currentDoor?.doorId ||
        currentDoor?.id ||
        historyDoorId ||
        null;

    const effectiveCurrentCollectionType =
        currentDoor?.type === "communal" ||
            currentDoor?.type === "door"
            ? "DoorReports"
            : "ResidentialReports";

    const originalDoorOwnerCacheRef = useRef(new Map());
    const buildingReportsCacheRef = useRef(new Map());

    const annotationConfigs = useMemo(
        () => ({
            doorPullPhotoLink: {
                annotationField: "pullAiAnnotations",
                imageName: "Pull Door Photo",
                regionClsList: DOOR_PART_CLASSES,
            },
            doorPushPhotoLink: {
                annotationField: "pushAiAnnotations",
                imageName: "Push Door Photo",
                regionClsList: DOOR_PART_CLASSES,
            },
            doorCertPhotoLink: {
                annotationField: "certAiAnnotations",
                imageName: "Certification Photo",
                regionClsList: DOOR_PART_CLASSES,
            },
            hingePhotoLink: {
                annotationField: "hingeAiAnnotations",
                imageName: "Hinge Photo",
                regionClsList: HINGE_CLASSES,
            },
            hingePhotoLink2: {
                annotationField: "hinge2AiAnnotations",
                imageName: "Hinge Photo 2",
                regionClsList: HINGE_CLASSES,
            },
            hingePhotoLink3: {
                annotationField: "hinge3AiAnnotations",
                imageName: "Hinge Photo 3",
                regionClsList: HINGE_CLASSES,
            },
            lockPhotoLink: {
                annotationField: "lockAiAnnotations",
                imageName: "Lock Photo",
                regionClsList: DOOR_PART_CLASSES,
            },
            lockPhotoLink2: {
                annotationField: "lock2AiAnnotations",
                imageName: "Lock Photo 2",
                regionClsList: DOOR_PART_CLASSES,
            },
            lockPhotoLink3: {
                annotationField: "lock3AiAnnotations",
                imageName: "Lock Photo 3",
                regionClsList: DOOR_PART_CLASSES,
            },
            letterBoxPhotoLink: {
                annotationField: "letterBoxAiAnnotations",
                imageName: "Letterbox Photo",
                regionClsList: DOOR_PART_CLASSES,
            },
            doorDamageLink: {
                annotationField: "damageAiAnnotations",
                imageName: "Damage Photo",
                regionClsList: DOOR_PART_CLASSES,
            },
            doorDamageLink2: {
                annotationField: "damage2AiAnnotations",
                imageName: "Damage Photo 2",
                regionClsList: DOOR_PART_CLASSES,
            },
            doorDamageLink3: {
                annotationField: "damage3AiAnnotations",
                imageName: "Damage Photo 3",
                regionClsList: DOOR_PART_CLASSES,
            },
            doorDamageLink4: {
                annotationField: "damage4AiAnnotations",
                imageName: "Damage Photo 4",
                regionClsList: DOOR_PART_CLASSES,
            },
            doorDamageLink5: {
                annotationField: "damage5AiAnnotations",
                imageName: "Damage Photo 5",
                regionClsList: DOOR_PART_CLASSES,
            },
            glassFireStampLink: {
                annotationField: "glassFireStampAiAnnotations",
                imageName: "Fire Stamp Photo",
                regionClsList: DOOR_PART_CLASSES,
            },
            cupboard_contense_photo: {
                annotationField: "cupboardContentsAiAnnotations",
                imageName: "Cupboard Contents Photo",
                regionClsList: DOOR_PART_CLASSES,
            },
            workDonePhoto: {
                annotationFieldPrefix: "workDoneAiAnnotations_",
                imageName: "Work Done Photo",
                regionClsList: DOOR_PART_CLASSES,
            },
        }),
        [],
    );

    const getResolvedAnnotationField = (
        source,
        config,
    ) => {
        if (config?.annotationField) {
            return config.annotationField;
        }

        if (config?.annotationFieldPrefix) {
            const workDoneIndex =
                Number.isInteger(source?.workDoneIndex)
                    ? source.workDoneIndex
                    : 0;

            return `${config.annotationFieldPrefix}${workDoneIndex}`;
        }

        return null;
    };

    const isVideoUrl = (url) => {
        if (!url) return false;

        const value = String(url).toLowerCase();
        const extensions = [".mp4", ".mov", ".avi", ".webm", ".mkv", ".m4v", ".3gp"];

        return extensions.some((ext) => value.includes(ext));
    };

    const parseDate = (value) => {
        if (!value) return null;

        try {
            if (typeof value?.toDate === "function") {
                return value.toDate();
            }

            if (typeof value === "object" && value?.seconds) {
                return new Date(value.seconds * 1000);
            }

            if (
                typeof value === "string" &&
                /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)
            ) {
                const [day, month, year] = value.split("/").map(Number);
                const parsed = new Date(year, month - 1, day);

                return Number.isNaN(parsed.getTime()) ? null : parsed;
            }

            const parsed = new Date(value);

            return Number.isNaN(parsed.getTime()) ? null : parsed;
        } catch {
            return null;
        }
    };

    const getDateKey = (value) => {
        const date = parseDate(value);

        if (!date) return "unknown";

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const getDateLabel = (value) => {
        const date = parseDate(value);

        if (!date) return "Unknown Date";

        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const getDefaultPhotos = (door) =>
        PDF_PHOTO_SLOTS.reduce((result, slot) => {
            result[slot.key] = door?.[slot.field] || null;
            return result;
        }, {});

    const buildCandidates = (currentReport, history = []) => {
        const result = [];

        const currentDate =
            currentReport?.date ||
            currentReport?.inspectionDate ||
            currentReport?.createdAt ||
            null;

        const currentDateKey = getDateKey(currentDate);

        const currentNormalUrls = new Set(
            [
                currentReport?.doorPullPhotoLink,
                currentReport?.doorPushPhotoLink,
                currentReport?.doorCertPhotoLink,
                currentReport?.hingePhotoLink,
                currentReport?.hingePhotoLink2,
                currentReport?.hingePhotoLink3,
                currentReport?.lockPhotoLink,
                currentReport?.lockPhotoLink2,
                currentReport?.lockPhotoLink3,
                currentReport?.letterBoxPhotoLink,
                currentReport?.doorDamageLink,
                currentReport?.doorDamageLink2,
                currentReport?.doorDamageLink3,
                currentReport?.doorDamageLink4,
                currentReport?.doorDamageLink5,
                currentReport?.glassFireStampLink,
                currentReport?.cupboard_contense_photo,
            ].filter(Boolean),
        );

        const currentWorkDoneUrls = new Set([
            ...(Array.isArray(currentReport?.workDonePhoto)
                ? currentReport.workDonePhoto
                : []),
            ...(Array.isArray(currentReport?.workDonePhotos)
                ? currentReport.workDonePhotos
                : []),
        ]);

        const addCandidate = ({
            url,
            title,
            groupRawDate,
            actualRawDate = null,
            sourceLabel,
            sourceField = null,
            sourceReportUid = null,
            sourceDoorId = null,
            sourceCollectionType = null,
            workDoneIndex = null,
            isCurrent = false,
            isWorkDone = false,
            historyIndex = null,
            mediaIndex = null,
            groupId = null,
        }) => {
            if (!url || isVideoUrl(url)) return;

            const dateKey = getDateKey(groupRawDate);

            const id = [
                groupId || "group",
                historyIndex ?? "history",
                mediaIndex ?? "media",
                isCurrent ? "current" : "previous",
                isWorkDone ? "work" : "survey",
                dateKey,
                url,
            ].join("::");

            result.push({
                id,
                url,
                title: title || "Photo",
                displayTitle: title || "Photo",
                rawDate: groupRawDate,
                dateKey,
                dateLabel: getDateLabel(groupRawDate),
                timestamp: parseDate(groupRawDate)?.getTime?.() || 0,
                actualRawDate: actualRawDate || groupRawDate,
                actualDateLabel: getDateLabel(actualRawDate || groupRawDate),
                isCurrent,
                isWorkDone,
                sourceLabel,
                sourceField,
                sourceReportUid:
                    isCurrent
                        ? (
                            effectiveCurrentReportUid ||
                            sourceReportUid ||
                            null
                        )
                        : (
                            sourceReportUid ||
                            null
                        ),
                sourceDoorId:
                    isCurrent
                        ? (
                            effectiveCurrentDoorId ||
                            sourceDoorId ||
                            null
                        )
                        : (
                            sourceDoorId ||
                            null
                        ),
                sourceCollectionType:
                    isCurrent
                        ? effectiveCurrentCollectionType
                        : (
                            sourceCollectionType ||
                            null
                        ),
                workDoneIndex,
            });
        };

        let lastSurveyContext = null;

        (history || []).forEach((entry, historyIndex) => {
            if (!entry) return;

            const entryDate =
                entry.date ||
                entry.inspectionDate ||
                entry.workDoneUpdatedAt ||
                entry.createdAt ||
                null;

            const media = Array.isArray(entry.media) ? entry.media : [];

            const entrySourceReportUid =
                entry.sourceReportUid ||
                entry._reportUid ||
                entry.reportUid ||
                null;

            const entrySourceDoorId =
                entry.sourceDoorId ||
                entry._doorId ||
                entry.doorId ||
                null;

            const isWorkDoneEntry =
                entry.type === "reinspect" || entry.label === "After Work Done";

            if (!isWorkDoneEntry) {
                const entryDateKey = getDateKey(entryDate);

                const hasCurrentImage = media.some(
                    (item) => item?.link && currentNormalUrls.has(item.link),
                );

                const isCurrentEntry =
                    entryDateKey === currentDateKey && hasCurrentImage;

                const groupId = `${isCurrentEntry ? "current" : "previous"
                    }-survey-${historyIndex}`;

                lastSurveyContext = {
                    rawDate: entryDate,
                    isCurrent: isCurrentEntry,
                    groupId,
                };

                media.forEach((item, mediaIndex) => {
                    if (!item?.link) return;
                    if (item.isVideo || isVideoUrl(item.link)) return;

                    const title = String(item.title || `Photo ${mediaIndex + 1}`);

                    if (title.toLowerCase().includes("work done")) return;

                    addCandidate({
                        url: item.link,
                        title,
                        groupRawDate: entryDate,
                        actualRawDate: entryDate,
                        sourceLabel: isCurrentEntry
                            ? "Current Survey"
                            : "Previous Survey",
                        sourceField:
                            item.field ||
                            item.sourceField ||
                            inferSourceFieldFromTitle(title),
                        sourceReportUid: isCurrentEntry
                            ? currentReportUid || entrySourceReportUid
                            : entrySourceReportUid,
                        sourceDoorId: isCurrentEntry
                            ? currentDoorId || entrySourceDoorId
                            : entrySourceDoorId,
                        sourceCollectionType:
                            entry.sourceCollectionType ||
                            entry.collectionType ||
                            null,
                        isCurrent: isCurrentEntry,
                        isWorkDone: false,
                        historyIndex,
                        mediaIndex,
                        groupId,
                    });
                });

                return;
            }

            const groupDate = lastSurveyContext?.rawDate || entryDate;
            const isCurrentGroup = lastSurveyContext?.isCurrent === true;
            const groupId =
                lastSurveyContext?.groupId || `previous-work-${historyIndex}`;

            const workDoneMedia = media.filter((item) => {
                if (!item?.link) return false;
                if (item.isVideo || isVideoUrl(item.link)) return false;

                return String(item.title || "")
                    .trim()
                    .toLowerCase()
                    .includes("work done");
            });

            workDoneMedia.forEach((item, mediaIndex) => {
                addCandidate({
                    url: item.link,
                    title: item.title || `Work Done ${mediaIndex + 1}`,
                    groupRawDate: groupDate,
                    actualRawDate: entryDate,
                    sourceLabel: isCurrentGroup
                        ? "Current Work Done"
                        : "Previous Work Done",
                    sourceField: item.field || item.sourceField || "workDonePhoto",
                    sourceReportUid: isCurrentGroup
                        ? currentReportUid || entrySourceReportUid
                        : entrySourceReportUid,
                    sourceDoorId: isCurrentGroup
                        ? currentDoorId || entrySourceDoorId
                        : entrySourceDoorId,
                    sourceCollectionType:
                        entry.sourceCollectionType ||
                        entry.collectionType ||
                        null,
                    workDoneIndex:
                        item.workDoneIndex ??
                        item.index ??
                        mediaIndex,
                    isCurrent: isCurrentGroup,
                    isWorkDone: true,
                    historyIndex,
                    mediaIndex,
                    groupId: `${groupId}-work`,
                });
            });
        });

        const currentMedia = [
            {
                url: currentReport?.doorPullPhotoLink,
                title: "Front",
                field: "doorPullPhotoLink",
            },
            {
                url: currentReport?.doorPushPhotoLink,
                title: "Back",
                field: "doorPushPhotoLink",
            },
            {
                url: currentReport?.doorCertPhotoLink,
                title: "Certification",
                field: "doorCertPhotoLink",
            },
            {
                url: currentReport?.hingePhotoLink,
                title: "Hinge 1",
                field: "hingePhotoLink",
            },
            {
                url: currentReport?.hingePhotoLink2,
                title: "Hinge 2",
                field: "hingePhotoLink2",
            },
            {
                url: currentReport?.hingePhotoLink3,
                title: "Hinge 3",
                field: "hingePhotoLink3",
            },
            {
                url: currentReport?.lockPhotoLink,
                title: "Lock 1",
                field: "lockPhotoLink",
            },
            {
                url: currentReport?.lockPhotoLink2,
                title: "Lock 2",
                field: "lockPhotoLink2",
            },
            {
                url: currentReport?.lockPhotoLink3,
                title: "Lock 3",
                field: "lockPhotoLink3",
            },
            {
                url: currentReport?.letterBoxPhotoLink,
                title: "Letterbox",
                field: "letterBoxPhotoLink",
            },
            {
                url: currentReport?.doorDamageLink,
                title: "Damage 1",
                field: "doorDamageLink",
            },
            {
                url: currentReport?.doorDamageLink2,
                title: "Damage 2",
                field: "doorDamageLink2",
            },
            {
                url: currentReport?.doorDamageLink3,
                title: "Damage 3",
                field: "doorDamageLink3",
            },
            {
                url: currentReport?.doorDamageLink4,
                title: "Damage 4",
                field: "doorDamageLink4",
            },
            {
                url: currentReport?.doorDamageLink5,
                title: "Damage 5",
                field: "doorDamageLink5",
            },
            {
                url: currentReport?.glassFireStampLink,
                title: "Fire Stamp",
                field: "glassFireStampLink",
            },
            {
                url: currentReport?.cupboard_contense_photo,
                title: "Cupboard Contents",
                field: "cupboard_contense_photo",
            },
        ];

        currentMedia.forEach((item, mediaIndex) => {
            if (!item.url) return;

            const exists = result.some(
                (candidate) =>
                    candidate.url === item.url &&
                    candidate.isCurrent &&
                    !candidate.isWorkDone,
            );

            if (exists) return;

            addCandidate({
                url: item.url,
                title: item.title,
                groupRawDate: currentDate,
                actualRawDate: currentDate,
                sourceLabel: "Current Survey",
                sourceField: item.field,
                sourceReportUid: currentReportUid,
                sourceDoorId: currentDoorId,
                isCurrent: true,
                isWorkDone: false,
                historyIndex: "current",
                mediaIndex,
                groupId: "current-fallback",
            });
        });

        [...currentWorkDoneUrls].forEach((url, mediaIndex) => {
            if (!url) return;

            const exists = result.some(
                (candidate) =>
                    candidate.url === url &&
                    candidate.isCurrent &&
                    candidate.isWorkDone,
            );

            if (exists) return;

            addCandidate({
                url,
                title: `Work Done ${mediaIndex + 1}`,
                groupRawDate: currentDate,
                actualRawDate: currentReport?.workDoneUpdatedAt || currentDate,
                sourceLabel: "Current Work Done",
                sourceField: "workDonePhoto",
                sourceReportUid: currentReportUid,
                sourceDoorId: currentDoorId,
                workDoneIndex: mediaIndex,
                isCurrent: true,
                isWorkDone: true,
                historyIndex: "current-work",
                mediaIndex,
                groupId: "current-work",
            });
        });

        return result.sort((a, b) => {
            if (b.timestamp !== a.timestamp) return b.timestamp - a.timestamp;
            if (a.isWorkDone !== b.isWorkDone) return a.isWorkDone ? 1 : -1;
            if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
            return 0;
        });
    };

    const getDefaultPhotoIds = (defaults, candidates) =>
        PDF_PHOTO_SLOTS.reduce((result, slot) => {
            const url = defaults[slot.key];

            if (!url) {
                result[slot.key] = null;
                return result;
            }

            const currentCandidate = candidates.find(
                (candidate) =>
                    candidate.url === url &&
                    candidate.isCurrent &&
                    !candidate.isWorkDone,
            );

            if (currentCandidate) {
                result[slot.key] = currentCandidate.id;
                return result;
            }

            const fallback = candidates.find(
                (candidate) => candidate.url === url,
            );

            result[slot.key] = fallback?.id || null;
            return result;
        }, {});

    const getCandidateCollectionType = (candidate) => {
        if (candidate?.sourceCollectionType) {
            return candidate.sourceCollectionType;
        }

        return effectiveCurrentCollectionType;
    };

    const doesDoorContainCandidate = (doorData, candidate) => {
        if (!doorData || !candidate?.url) {
            return false;
        }

        const sourceField =
            candidate.sourceField ||
            inferSourceFieldFromTitle(candidate.title);

        if (sourceField === "workDonePhoto") {
            const workDoneUrls = [
                ...(Array.isArray(doorData?.workDonePhoto)
                    ? doorData.workDonePhoto
                    : []),
                ...(Array.isArray(doorData?.workDonePhotos)
                    ? doorData.workDonePhotos
                    : []),
            ];

            return workDoneUrls.includes(candidate.url);
        }

        if (sourceField && doorData?.[sourceField] === candidate.url) {
            return true;
        }

        return ALL_SOURCE_IMAGE_FIELDS.some(
            (field) => doorData?.[field] === candidate.url,
        );
    };

    const findMatchedSourceField = (doorData, candidate) => {
        const declaredField =
            candidate?.sourceField ||
            inferSourceFieldFromTitle(candidate?.title);

        if (declaredField === "workDonePhoto") {
            return "workDonePhoto";
        }

        if (
            declaredField &&
            doorData?.[declaredField] === candidate?.url
        ) {
            return declaredField;
        }

        const matchedField = ALL_SOURCE_IMAGE_FIELDS.find(
            (field) => doorData?.[field] === candidate?.url,
        );

        if (matchedField) {
            return matchedField;
        }

        const workDoneUrls = [
            ...(Array.isArray(doorData?.workDonePhoto)
                ? doorData.workDonePhoto
                : []),
            ...(Array.isArray(doorData?.workDonePhotos)
                ? doorData.workDonePhotos
                : []),
        ];

        if (workDoneUrls.includes(candidate?.url)) {
            return "workDonePhoto";
        }

        return declaredField || null;
    };

    const hasAddedBy = (doorData) =>
        String(doorData?.addedBy ?? "").trim().length > 0;

    const collectionOrder = (preferredCollection) => {
        const preferred =
            preferredCollection === "DoorReports"
                ? "DoorReports"
                : "ResidentialReports";

        return [
            preferred,
            preferred === "ResidentialReports"
                ? "DoorReports"
                : "ResidentialReports",
        ];
    };

    const readDoorFromReport = async ({
        reportUid,
        doorId,
        preferredCollection,
    }) => {
        if (!reportUid || !doorId) {
            return null;
        }

        for (const collectionType of collectionOrder(preferredCollection)) {
            const doorRef = doc(
                db,
                `Reports/${reportUid}/${collectionType}/${doorId}`,
            );

            const doorSnap = await getDoc(doorRef);

            if (doorSnap.exists()) {
                return {
                    reportUid,
                    doorId,
                    collectionType,
                    data: doorSnap.data() || {},
                };
            }
        }

        return null;
    };

    const getBuildingReports = async () => {
        const buildingName =
            buildingData?.buildingName ||
            currentDoor?.buildingName ||
            null;

        if (!buildingName) {
            return [];
        }

        if (buildingReportsCacheRef.current.has(buildingName)) {
            return buildingReportsCacheRef.current.get(buildingName);
        }

        const request = getDocs(
            query(
                collection(db, "Reports"),
                where("buildingName", "==", buildingName),
            ),
        ).then((snapshot) =>
            snapshot.docs
                .map((reportDoc) => ({
                    id: reportDoc.id,
                    data: reportDoc.data() || {},
                }))
                .sort((a, b) => {
                    const dateA =
                        parseDate(a.data?.date)?.getTime?.() || 0;
                    const dateB =
                        parseDate(b.data?.date)?.getTime?.() || 0;

                    return dateB - dateA;
                }),
        );

        buildingReportsCacheRef.current.set(buildingName, request);

        return request;
    };

    const makeResolvedSource = (candidate, doorRecord) => ({
        ...candidate,
        sourceReportUid: doorRecord.reportUid,
        sourceDoorId: doorRecord.doorId,
        sourceCollectionType: doorRecord.collectionType,
        sourceField:
            findMatchedSourceField(
                doorRecord.data,
                candidate,
            ) ||
            candidate?.sourceField ||
            null,
    });

    const resolveOriginalCurrentDoorSource = async (candidate) => {
        const buildingReportUid =
            currentBuildingReportUid;

        const doorId =
            currentDoorId ||
            candidate?.sourceDoorId ||
            currentDoor?._doorId ||
            currentDoor?.doorId ||
            currentDoor?.id ||
            historyDoorId ||
            null;

        if (!buildingReportUid || !doorId) {
            return null;
        }

        const preferredCollection =
            candidate?.sourceCollectionType ||
            effectiveCurrentCollectionType;

        const cacheKey = [
            buildingReportUid,
            preferredSourceReportUid || "",
            preferredCollection,
            doorId,
        ].join("::");

        if (originalDoorOwnerCacheRef.current.has(cacheKey)) {
            return makeResolvedSource(
                candidate,
                originalDoorOwnerCacheRef.current.get(cacheKey),
            );
        }

        if (
            preferredSourceReportUid &&
            preferredSourceReportUid !== buildingReportUid
        ) {
            const preferredDoorRecord =
                await readDoorFromReport({
                    reportUid: preferredSourceReportUid,
                    doorId,
                    preferredCollection,
                });

            if (
                preferredDoorRecord &&
                hasAddedBy(preferredDoorRecord.data)
            ) {
                originalDoorOwnerCacheRef.current.set(
                    cacheKey,
                    preferredDoorRecord,
                );

                return makeResolvedSource(
                    candidate,
                    preferredDoorRecord,
                );
            }
        }

        const currentDoorRecord =
            await readDoorFromReport({
                reportUid: buildingReportUid,
                doorId,
                preferredCollection,
            });

        if (
            currentDoorRecord &&
            hasAddedBy(currentDoorRecord.data)
        ) {
            originalDoorOwnerCacheRef.current.set(
                cacheKey,
                currentDoorRecord,
            );

            return makeResolvedSource(
                candidate,
                currentDoorRecord,
            );
        }

        const reports =
            await getBuildingReports();

        for (const reportEntry of reports) {
            if (reportEntry.id === buildingReportUid) {
                continue;
            }

            const historicalDoorRecord =
                await readDoorFromReport({
                    reportUid: reportEntry.id,
                    doorId,
                    preferredCollection,
                });

            if (
                !historicalDoorRecord ||
                !hasAddedBy(historicalDoorRecord.data)
            ) {
                continue;
            }

            originalDoorOwnerCacheRef.current.set(
                cacheKey,
                historicalDoorRecord,
            );

            return makeResolvedSource(
                candidate,
                historicalDoorRecord,
            );
        }

        return null;
    };


    const resolveHistoricalSource = async (candidate) => {
        const doorId =
            candidate?.sourceDoorId ||
            effectiveCurrentDoorId;

        const preferredCollection =
            candidate?.sourceCollectionType ||
            effectiveCurrentCollectionType;

        if (
            candidate?.sourceReportUid &&
            doorId
        ) {
            const exactDoorRecord =
                await readDoorFromReport({
                    reportUid:
                        candidate.sourceReportUid,
                    doorId,
                    preferredCollection,
                });

            if (exactDoorRecord) {
                return makeResolvedSource(
                    candidate,
                    exactDoorRecord,
                );
            }
        }

        if (!doorId) {
            return null;
        }

        const reports = await getBuildingReports();

        for (const reportEntry of reports) {
            const doorRecord =
                await readDoorFromReport({
                    reportUid: reportEntry.id,
                    doorId,
                    preferredCollection,
                });

            if (
                doorRecord &&
                doesDoorContainCandidate(
                    doorRecord.data,
                    candidate,
                )
            ) {
                return makeResolvedSource(
                    candidate,
                    doorRecord,
                );
            }
        }

        return null;
    };

    const resolveCandidateSource = async (candidate) => {
        if (!candidate?.url || !db) {
            return null;
        }

        if (
            candidate.isCurrent &&
            !candidate.isWorkDone
        ) {
            return resolveOriginalCurrentDoorSource(
                candidate,
            );
        }

        const historicalSource =
            await resolveHistoricalSource(
                candidate,
            );

        if (historicalSource) {
            return historicalSource;
        }

        if (candidate.isCurrent) {
            return resolveOriginalCurrentDoorSource(
                candidate,
            );
        }

        return null;
    };


    const mapRegionsForFirestore = (
        regions = [],
    ) => {
        return regions.map((region) => {
            const commonData = {
                cls: region.cls || "",
                type: region.type || "box",
            };

            if (
                region.type === "polygon"
            ) {
                const points =
                    region.points ||
                    (region.pointsX || []).map(
                        (x, index) => [
                            x,
                            region.pointsY?.[
                            index
                            ],
                        ],
                    );

                return {
                    ...commonData,
                    type: "polygon",
                    pointsX: points.map(
                        (point) => point[0],
                    ),
                    pointsY: points.map(
                        (point) => point[1],
                    ),
                };
            }

            return {
                ...commonData,
                x: region.x || 0,
                y: region.y || 0,
                w: region.w || 0,
                h: region.h || 0,
            };
        });
    };

    const mapRegionsForModal = (
        regions = [],
    ) => {
        return regions.map(
            (region, index) => {
                if (
                    region.type ===
                    "polygon"
                ) {
                    return {
                        ...region,
                        id: `region_${index}`,
                        color:
                            classColors[
                            region.cls
                            ] || "#dc2626",
                        type: "polygon",
                        points:
                            region.points ||
                            (
                                region.pointsX ||
                                []
                            ).map(
                                (x, pointIndex) => [
                                    x,
                                    region.pointsY?.[
                                    pointIndex
                                    ],
                                ],
                            ),
                    };
                }

                return {
                    ...region,
                    id: `region_${index}`,
                    color:
                        classColors[
                        region.cls
                        ] || "#dc2626",
                    type:
                        region.type ||
                        "box",
                };
            },
        );
    };

    const getAnnotationDocRef = (
        source,
        config,
    ) => {
        const annotationField =
            getResolvedAnnotationField(
                source,
                config,
            );

        if (
            !db ||
            !source?.sourceReportUid ||
            !source?.sourceDoorId ||
            !annotationField
        ) {
            return null;
        }

        const collectionType =
            source.sourceCollectionType ||
            getCandidateCollectionType(
                source,
            );

        return doc(
            db,
            `Reports/${source.sourceReportUid}/${collectionType}/${source.sourceDoorId}/ai-annotations/${annotationField}`,
        );
    };

    const saveRegionsToSource = async ({
        source,
        config,
        regions,
    }) => {
        const annotationDocRef =
            getAnnotationDocRef(
                source,
                config,
            );

        if (!annotationDocRef) {
            throw new Error(
                "Unable to determine source report/door for annotation.",
            );
        }

        const existingDoc =
            await getDoc(
                annotationDocRef,
            );

        const createdAt =
            existingDoc.exists() &&
                existingDoc.data()?.createdAt
                ? existingDoc.data()
                    .createdAt
                : serverTimestamp();

        const mappedRegions =
            mapRegionsForFirestore(
                regions,
            );

        await setDoc(
            annotationDocRef,
            {
                regions: mappedRegions,
                updatedAt: serverTimestamp(),
                createdAt,
            },
        );

        return mappedRegions;
    };

    const getSelectedCandidateForSlot = (
        slot,
    ) => {
        if (!slot) {
            return null;
        }

        const url =
            selectedPhotos[
            slot.key
            ];

        if (!url) {
            return null;
        }

        const candidateId =
            selectedPhotoIds[
            slot.key
            ];

        const candidate =
            candidateId
                ? photoCandidates.find(
                    (item) =>
                        item.id ===
                        candidateId,
                )
                : null;

        if (candidate) {
            return candidate;
        }

        const selectedSource =
            selectedPhotoSources[
            slot.key
            ] || {};

        return {
            id:
                `fallback-${slot.key}`,
            url,
            title:
                slot.label,
            displayTitle:
                slot.label,
            sourceField:
                selectedSource.sourceField ||
                slot.field,
            sourceReportUid:
                (selectedSource.isCurrent ?? true)
                    ? (
                        effectiveCurrentReportUid ||
                        selectedSource.sourceReportUid ||
                        null
                    )
                    : (
                        selectedSource.sourceReportUid ||
                        null
                    ),
            sourceDoorId:
                (selectedSource.isCurrent ?? true)
                    ? (
                        effectiveCurrentDoorId ||
                        selectedSource.sourceDoorId ||
                        null
                    )
                    : (
                        selectedSource.sourceDoorId ||
                        null
                    ),
            sourceCollectionType:
                (selectedSource.isCurrent ?? true)
                    ? effectiveCurrentCollectionType
                    : (
                        selectedSource.sourceCollectionType ||
                        null
                    ),
            isCurrent:
                selectedSource.isCurrent ??
                true,
            isWorkDone:
                selectedSource.isWorkDone ??
                false,
            workDoneIndex:
                selectedSource.workDoneIndex ??
                null,
            sourceLabel:
                selectedSource.sourceLabel ||
                "Current Survey",
        };
    };


    const handleOpenAnnotateModal = async (slot) => {
        const candidate =
            getSelectedCandidateForSlot(slot);

        /*
         * The original/source image decides where annotations
         * are loaded from and saved to.
         */
        const sourceField =
            candidate?.sourceField ||
            slot?.field;

        const sourceConfig =
            annotationConfigs[sourceField];

        /*
         * The destination PDF slot decides which labels/classes
         * are available in the annotation modal.
         *
         * Front + Back = door-part classes
         * Hinge        = hinge classes
         */
        const destinationRegionClsList =
            slot?.field === "hingePhotoLink"
                ? HINGE_CLASSES
                : (
                    slot?.field === "doorPullPhotoLink" ||
                    slot?.field === "doorPushPhotoLink"
                )
                    ? DOOR_PART_CLASSES
                    : [];

        const photoLink =
            selectedPhotos[slot?.key] ||
            candidate?.url ||
            null;

        if (!sourceConfig || !photoLink) {
            toast.warning(
                "This selected photo type does not have an annotation storage mapping.",
            );
            return;
        }

        if (destinationRegionClsList.length === 0) {
            toast.warning(
                "Annotations are only available for Front, Back, and Hinge slots.",
            );
            return;
        }

        if (!db) {
            toast.error(
                "Annotation service is unavailable.",
            );
            return;
        }

        const requestId =
            ++annotationLoadRequestRef.current;

        setAnnotationLoadError(null);
        setShowAnnotateModalResult(null);
        setAnnotationLoadingInfo({
            imageSrc: photoLink,
            imageName: slot?.label || sourceConfig.imageName,
            sourceLabel:
                candidate?.sourceLabel ||
                "Selected photo",
        });
        setLoadingAnnotation(true);
        setShowManualAnnotateModal(true);

        try {
            const source =
                await resolveCandidateSource({
                    ...candidate,
                    url: photoLink,
                });

            if (
                annotationLoadRequestRef.current !==
                requestId
            ) {
                return;
            }

            if (
                !source?.sourceReportUid ||
                !source?.sourceDoorId ||
                !source?.sourceCollectionType
            ) {
                throw new Error(
                    effectiveCurrentDoorId
                        ? "The original door report could not be found."
                        : "The door document ID is unavailable. Pass currentDoorId to PhotoSelector.",
                );
            }

            const annotationDocRef =
                getAnnotationDocRef(
                    source,
                    sourceConfig,
                );

            if (!annotationDocRef) {
                throw new Error(
                    "The annotation document path could not be resolved.",
                );
            }

            const annotationSnap =
                await getDoc(annotationDocRef);

            if (
                annotationLoadRequestRef.current !==
                requestId
            ) {
                return;
            }

            const rawRegions =
                annotationSnap.exists() &&
                    Array.isArray(
                        annotationSnap.data()?.regions,
                    )
                    ? annotationSnap.data().regions
                    : [];

            const existingRegions =
                mapRegionsForModal(rawRegions);

            setCurrentAnnotatingDoor(source);
            setShowAnnotateModalResult({
                regions: existingRegions,
                imageSrc: photoLink,
                imageName: slot?.label || sourceConfig.imageName,
                regionClsList:
                    destinationRegionClsList,
                annotationField:
                    getResolvedAnnotationField(
                        source,
                        sourceConfig,
                    ),
                source,
                config:
                    sourceConfig,
            });
        } catch (error) {
            console.error(
                "Failed to load annotations:",
                error,
            );

            setAnnotationLoadError(
                error?.message ||
                "Failed to load annotations.",
            );

            setShowAnnotateModalResult(null);
        } finally {
            if (
                annotationLoadRequestRef.current ===
                requestId
            ) {
                setLoadingAnnotation(false);
            }
        }
    };


    const saveManualAnnotation = async ({
        regions = [],
    }) => {
        if (
            !showAnnotateModalResult ||
            !currentAnnotatingDoor ||
            savingAnnotation
        ) {
            return;
        }

        try {
            setSavingAnnotation(true);

            const source =
                showAnnotateModalResult.source ||
                currentAnnotatingDoor;

            const resolvedSource =
                source?.sourceReportUid &&
                    source?.sourceDoorId &&
                    source?.sourceCollectionType
                    ? source
                    : await resolveCandidateSource(source);

            if (
                !resolvedSource?.sourceReportUid ||
                !resolvedSource?.sourceDoorId ||
                !resolvedSource?.sourceCollectionType
            ) {
                throw new Error(
                    "The original door report could not be resolved.",
                );
            }

            const config =
                showAnnotateModalResult.config;

            if (!config) {
                throw new Error(
                    "Annotation configuration is unavailable.",
                );
            }

            await saveRegionsToSource({
                source: resolvedSource,
                config,
                regions,
            });

            toast.success(
                "Annotation saved successfully!",
            );

            setShowManualAnnotateModal(false);
            setCurrentAnnotatingDoor(null);
            setShowAnnotateModalResult(null);
            setAnnotationLoadingInfo(null);
        } catch (error) {
            console.error(
                "Failed to save annotations:",
                error,
            );

            toast.error(
                "Failed to save annotations.",
            );
        } finally {
            setSavingAnnotation(false);
        }
    };


    const buildSelectedAnnotations = async () => {
        const aiAnnotations = [];
        const resolvedSources = {};

        for (const slot of PDF_PHOTO_SLOTS) {
            const url =
                selectedPhotos[slot.key];

            if (!url) {
                resolvedSources[slot.field] = null;
                continue;
            }

            const candidate =
                getSelectedCandidateForSlot(slot);

            const source =
                candidate
                    ? await resolveCandidateSource(candidate)
                    : null;

            resolvedSources[slot.field] =
                source
                    ? {
                        url: source.url,
                        sourceField:
                            source.sourceField,
                        sourceReportUid:
                            source.sourceReportUid,
                        sourceDoorId:
                            source.sourceDoorId,
                        sourceCollectionType:
                            source.sourceCollectionType,
                        workDoneIndex:
                            source.workDoneIndex ??
                            candidate?.workDoneIndex ??
                            null,
                        isCurrent:
                            !!source.isCurrent,
                        isWorkDone:
                            !!source.isWorkDone,
                        destinationField:
                            slot.field,
                        destinationSlot:
                            slot.key,
                        destinationLabel:
                            slot.label,
                    }
                    : null;

            const destinationAnnotationField =
                DESTINATION_ANNOTATION_FIELDS[
                slot.field
                ];

            if (!destinationAnnotationField) {
                continue;
            }

            const sourceField =
                source?.sourceField ||
                candidate?.sourceField ||
                slot.field;

            const sourceConfig =
                annotationConfigs[
                sourceField
                ];

            let regions = [];
            let annotationExists = false;

            if (
                sourceConfig &&
                source?.sourceReportUid &&
                source?.sourceDoorId &&
                source?.sourceCollectionType
            ) {
                try {
                    const annotationDocRef =
                        getAnnotationDocRef(
                            source,
                            sourceConfig,
                        );

                    if (annotationDocRef) {
                        const snap =
                            await getDoc(
                                annotationDocRef,
                            );

                        if (snap.exists()) {
                            const data =
                                snap.data() || {};

                            regions =
                                Array.isArray(
                                    data.regions,
                                )
                                    ? data.regions
                                    : [];

                            annotationExists =
                                true;
                        }
                    }
                } catch (error) {
                    console.error(
                        "Failed to load selected annotation:",
                        error,
                    );
                }
            }

            aiAnnotations.push({
                id:
                    destinationAnnotationField,
                regions,
                annotationExists,

                annotationField:
                    destinationAnnotationField,

                sourceAnnotationField:
                    getResolvedAnnotationField(
                        source,
                        sourceConfig,
                    ),

                sourceField,
                sourceImageUrl:
                    source?.url ||
                    url,
                imageUrl:
                    source?.url ||
                    url,

                destinationField:
                    slot.field,
                destinationSlot:
                    slot.key,
                destinationLabel:
                    slot.label,

                sourceReportUid:
                    source?.sourceReportUid ||
                    null,
                sourceDoorId:
                    source?.sourceDoorId ||
                    null,
                sourceCollectionType:
                    source?.sourceCollectionType ||
                    null,
            });
        }

        return {
            aiAnnotations,
            resolvedSources,
        };
    };


    const buildPhotoSourcesFromIds = (
        ids,
        defaults,
        candidates,
    ) => {
        return PDF_PHOTO_SLOTS.reduce((result, slot) => {
            const candidateId = ids?.[slot.key];
            const candidate = candidateId
                ? candidates.find((item) => item.id === candidateId)
                : null;

            if (candidate) {
                result[slot.key] = {
                    url: candidate.url,
                    sourceField: candidate.sourceField || slot.field,
                    sourceReportUid:
                        candidate.isCurrent
                            ? (
                                effectiveCurrentReportUid ||
                                candidate.sourceReportUid ||
                                null
                            )
                            : (
                                candidate.sourceReportUid ||
                                null
                            ),
                    sourceDoorId:
                        candidate.isCurrent
                            ? (
                                effectiveCurrentDoorId ||
                                candidate.sourceDoorId ||
                                null
                            )
                            : (
                                candidate.sourceDoorId ||
                                null
                            ),
                    sourceCollectionType:
                        candidate.sourceCollectionType || null,
                    workDoneIndex: candidate.workDoneIndex ?? null,
                    isWorkDone: !!candidate.isWorkDone,
                    isCurrent: !!candidate.isCurrent,
                    candidateId: candidate.id,
                    sourceLabel: candidate.sourceLabel,
                };

                return result;
            }

            const url = defaults?.[slot.key] || null;

            result[slot.key] = url
                ? {
                    url,
                    sourceField: slot.field,
                    sourceReportUid:
                        effectiveCurrentReportUid,
                    sourceDoorId:
                        effectiveCurrentDoorId,
                    sourceCollectionType:
                        currentDoor?.type === "residential"
                            ? "ResidentialReports"
                            : "DoorReports",
                    workDoneIndex: null,
                    isWorkDone: false,
                    isCurrent: true,
                    candidateId: null,
                    sourceLabel: "Current Survey",
                }
                : null;

            return result;
        }, {});
    };

    useEffect(() => {
        if (!show || !currentDoor) return;

        originalDoorOwnerCacheRef.current.clear();
        buildingReportsCacheRef.current.clear();

        const candidates = buildCandidates(currentDoor, doorHistory || []);
        const defaults = getDefaultPhotos(currentDoor);
        const ids = getDefaultPhotoIds(defaults, candidates);
        const sources = buildPhotoSourcesFromIds(
            ids,
            defaults,
            candidates,
        );

        setPhotoCandidates(candidates);
        setSelectedPhotos(defaults);
        setDefaultPhotos(defaults);
        setSelectedPhotoIds(ids);
        setDefaultPhotoIds(ids);
        setSelectedPhotoSources(sources);
        setDefaultPhotoSources(sources);
        setSelectedDate("all");
        setGenerating(false);

        const firstMissing = PDF_PHOTO_SLOTS.find(
            (slot) => !defaults[slot.key],
        );

        setActiveSlot(firstMissing?.key || "front");
    }, [
        show,
        report,
        doorHistory,
        effectiveCurrentReportUid,
        effectiveCurrentDoorId,
        currentBuildingReportUid,
        preferredSourceReportUid,
        buildingData?.uid,
    ]);

    const dateTabs = useMemo(() => {
        const map = new Map();

        photoCandidates.forEach((candidate) => {
            if (!map.has(candidate.dateKey)) {
                map.set(candidate.dateKey, {
                    key: candidate.dateKey,
                    label: candidate.dateLabel,
                    timestamp: candidate.timestamp,
                    count: 1,
                    hasCurrent: candidate.isCurrent,
                });
            } else {
                const existing = map.get(candidate.dateKey);
                existing.count += 1;

                if (candidate.isCurrent) {
                    existing.hasCurrent = true;
                }
            }
        });

        return [...map.values()].sort((a, b) => {
            if (a.key === "unknown") return 1;
            if (b.key === "unknown") return -1;
            return b.timestamp - a.timestamp;
        });
    }, [photoCandidates]);

    const dateGroups = useMemo(() => {
        const map = new Map();

        photoCandidates.forEach((candidate) => {
            if (!map.has(candidate.dateKey)) {
                map.set(candidate.dateKey, {
                    key: candidate.dateKey,
                    dateLabel: candidate.dateLabel,
                    timestamp: candidate.timestamp,
                    photos: [],
                });
            }

            map.get(candidate.dateKey).photos.push(candidate);
        });

        return [...map.values()].sort((a, b) => {
            if (a.key === "unknown") return 1;
            if (b.key === "unknown") return -1;
            return b.timestamp - a.timestamp;
        });
    }, [photoCandidates]);

    const visiblePhotos =
        selectedDate === "all"
            ? photoCandidates
            : photoCandidates.filter(
                (candidate) => candidate.dateKey === selectedDate,
            );

    const selectPhoto = (candidate) => {
        if (!activeSlot || !candidate?.url) return;

        const nextPhotos = {
            ...selectedPhotos,
            [activeSlot]: candidate.url,
        };

        const nextIds = {
            ...selectedPhotoIds,
            [activeSlot]: candidate.id,
        };

        const nextSources = {
            ...selectedPhotoSources,
            [activeSlot]: {
                url: candidate.url,
                sourceField:
                    candidate.sourceField ||
                    PDF_PHOTO_SLOTS.find((slot) => slot.key === activeSlot)?.field ||
                    null,
                sourceReportUid:
                    candidate.isCurrent
                        ? (
                            effectiveCurrentReportUid ||
                            candidate.sourceReportUid ||
                            null
                        )
                        : (
                            candidate.sourceReportUid ||
                            null
                        ),
                sourceDoorId:
                    candidate.isCurrent
                        ? (
                            effectiveCurrentDoorId ||
                            candidate.sourceDoorId ||
                            null
                        )
                        : (
                            candidate.sourceDoorId ||
                            null
                        ),
                sourceCollectionType:
                    candidate.isCurrent
                        ? effectiveCurrentCollectionType
                        : (
                            candidate.sourceCollectionType ||
                            null
                        ),
                workDoneIndex: candidate.workDoneIndex ?? null,
                isWorkDone: !!candidate.isWorkDone,
                isCurrent: !!candidate.isCurrent,
                candidateId: candidate.id,
                sourceLabel: candidate.sourceLabel,
            },
        };

        setSelectedPhotos(nextPhotos);
        setSelectedPhotoIds(nextIds);
        setSelectedPhotoSources(nextSources);

        const currentIndex = PDF_PHOTO_SLOTS.findIndex(
            (slot) => slot.key === activeSlot,
        );

        const nextAfter = PDF_PHOTO_SLOTS.slice(currentIndex + 1).find(
            (slot) => !nextPhotos[slot.key],
        );

        const nextBefore = PDF_PHOTO_SLOTS.slice(0, currentIndex).find(
            (slot) => !nextPhotos[slot.key],
        );

        const next = nextAfter || nextBefore;

        if (next) setActiveSlot(next.key);
    };

    const clearSlot = (key) => {
        setSelectedPhotos((previous) => ({
            ...previous,
            [key]: null,
        }));

        setSelectedPhotoIds((previous) => ({
            ...previous,
            [key]: null,
        }));

        setSelectedPhotoSources((previous) => ({
            ...previous,
            [key]: null,
        }));

        setActiveSlot(key);
    };

    const resetPhotos = () => {
        setSelectedPhotos(defaultPhotos);
        setSelectedPhotoIds(defaultPhotoIds);
        setSelectedPhotoSources(defaultPhotoSources);

        const firstMissing = PDF_PHOTO_SLOTS.find(
            (slot) => !defaultPhotos[slot.key],
        );

        setActiveSlot(firstMissing?.key || "front");
    };

    const getUsedBy = (candidate) => {
        if (!candidate?.id) return [];

        return PDF_PHOTO_SLOTS.filter(
            (slot) => selectedPhotoIds[slot.key] === candidate.id,
        ).map((slot) => slot.label);
    };

    const selectedCount = PDF_PHOTO_SLOTS.filter(
        (slot) => !!selectedPhotos[slot.key],
    ).length;

    const handleGenerate = async () => {
        if (generating) return;

        const selectedFields =
            PDF_PHOTO_SLOTS.reduce(
                (result, slot) => {
                    result[
                        slot.field
                    ] =
                        selectedPhotos[
                        slot.key
                        ] || null;

                    return result;
                },
                {},
            );

        try {
            setGenerating(true);

            const {
                aiAnnotations,
                resolvedSources,
            } =
                await buildSelectedAnnotations();

            await onGenerate?.({
                ...selectedFields,
                selectedFields,
                selectedSources:
                    resolvedSources,
                aiAnnotations,
            });
        } finally {
            setGenerating(false);
        }
    };

    const backdropStyle = {
        position: "fixed",
        inset: 0,
        zIndex: 1060,
        display: "flex",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: "center",
        padding: isMobile ? 0 : isTablet ? "10px" : "18px",
        background: "rgba(15, 23, 42, 0.72)",
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
    };

    const modalStyle = {
        width: isMobile ? "100%" : "min(1240px, 100%)",
        height: isMobile ? "100vh" : "auto",
        maxHeight: isMobile
            ? "100vh"
            : isTablet
                ? "calc(100vh - 20px)"
                : "calc(100vh - 36px)",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        border: "1px solid rgba(220, 38, 38, 0.12)",
        borderRadius: isMobile ? 0 : "18px",
        overflow: "hidden",
    };

    const headerStyle = {
        minHeight: "72px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "15px",
        padding: isMobile ? "13px 14px" : "16px 20px",
        background: "linear-gradient(135deg, #fff 0%, #fff7f7 100%)",
        borderBottom: "1px solid #e5e7eb",
    };

    const bodyStyle = isTablet
        ? {
            flex: 1,
            minHeight: 0,
            display: "block",
            overflowY: "auto",
        }
        : {
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: "330px 1fr",
            overflow: "hidden",
        };

    const leftStyle = {
        minHeight: 0,
        padding: isMobile ? "13px" : "17px",
        overflowY: isTablet ? "visible" : "auto",
        background: "#fafafa",
        borderRight: isTablet ? 0 : "1px solid #f1f1f1",
        borderBottom: isTablet ? "1px solid #e5e7eb" : 0,
    };

    const slotListStyle = isTablet
        ? {
            display: "grid",
            gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(2, minmax(0, 1fr))",
            gap: "8px",
        }
        : {
            display: "flex",
            flexDirection: "column",
            gap: "8px",
        };

    const rightStyle = {
        minWidth: 0,
        minHeight: isTablet ? "520px" : 0,
        display: "flex",
        flexDirection: "column",
        padding: isMobile ? "14px 13px" : "17px 18px",
        background: "#fff",
    };

    const photoGridStyle = {
        display: "grid",
        gridTemplateColumns: isMobile
            ? "repeat(2, minmax(0, 1fr))"
            : "repeat(auto-fill, minmax(150px, 1fr))",
        gap: isMobile ? "8px" : "11px",
    };

    const renderPhotoCard = (candidate) => {
        const usedBy = getUsedBy(candidate);
        const selected = usedBy.length > 0;
        const hovered = hoveredCard === candidate.id;

        return (
            <div
                key={candidate.id}
                role="button"
                tabIndex={0}
                onMouseEnter={() => setHoveredCard(candidate.id)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => selectPhoto(candidate)}
                onKeyDown={(event) => {
                    if (
                        event.key === "Enter" ||
                        event.key === " "
                    ) {
                        event.preventDefault();
                        selectPhoto(candidate);
                    }
                }}
                style={{
                    minWidth: 0,
                    padding: 0,
                    overflow: "hidden",
                    textAlign: "left",
                    border: `2px solid ${selected
                        ? "#18181b"
                        : hovered
                            ? "#dc2626"
                            : "#e5e7eb"
                        }`,
                    borderRadius: "12px",
                    background: "#fff",
                    cursor: "pointer",
                    transform:
                        hovered
                            ? "scale(0.96)"
                            : "scale(1)",
                    transformOrigin: "center center",
                    transition:
                        "border-color 0.17s ease, background-color 0.17s ease, transform 0.17s ease",
                }}
            >
                <div
                    style={{
                        position: "relative",
                        height: isMobile ? "110px" : "125px",
                        overflow: "hidden",
                        background: "#f4f4f5",
                    }}
                >
                    <img
                        src={candidate.url}
                        alt={candidate.displayTitle}
                        loading="lazy"
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "block",
                            objectFit: "cover",
                        }}
                    />

                    {candidate.isCurrent && !candidate.isWorkDone && (
                        <span
                            style={{
                                position: "absolute",
                                top: "7px",
                                left: "7px",
                                zIndex: 2,
                                padding: "4px 7px",
                                borderRadius: "999px",
                                color: "#fff",
                                fontSize: "8px",
                                fontWeight: 750,
                                background: "#18181b",
                            }}
                        >
                            Current
                        </span>
                    )}

                    {selected && (
                        <div
                            style={{
                                position: "absolute",
                                right: "7px",
                                bottom: "7px",
                                zIndex: 2,
                                maxWidth: "calc(100% - 14px)",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "4px 7px",
                                overflow: "hidden",
                                borderRadius: "999px",
                                background: "rgba(24, 24, 27, 0.95)",
                                color: "#fff",
                                fontSize: "8px",
                                fontWeight: 750,
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                            }}
                        >
                            <FontAwesomeIcon icon={faCheck} />
                            {usedBy.join(", ")}
                        </div>
                    )}
                </div>

                <div style={{ padding: "8px 9px 9px" }}>
                    <div
                        title={candidate.displayTitle}
                        style={{
                            overflow: "hidden",
                            color: "#27272a",
                            fontSize: "11px",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {candidate.displayTitle}
                    </div>

                    <div
                        style={{
                            marginTop: "2px",
                            overflow: "hidden",
                            color: "#a1a1aa",
                            fontSize: "9px",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {candidate.sourceLabel}

                        {candidate.isWorkDone &&
                            candidate.actualDateLabel &&
                            candidate.actualDateLabel !== candidate.dateLabel && (
                                <>
                                    {" • "}
                                    {candidate.actualDateLabel}
                                </>
                            )}
                    </div>
                </div>
            </div>
        );
    };

    const renderEmptyState = (message) => (
        <div
            style={{
                minHeight: "260px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
                color: "#9ca3af",
                textAlign: "center",
            }}
        >
            <FontAwesomeIcon
                icon={faImage}
                style={{
                    marginBottom: "5px",
                    color: "#fca5a5",
                    fontSize: "29px",
                }}
            />

            <div
                style={{
                    color: "#6b7280",
                    fontSize: "13px",
                    fontWeight: 700,
                }}
            >
                No photos
            </div>

            <div style={{ fontSize: "10px" }}>{message}</div>
        </div>
    );

    if (!show || !currentDoor) return null;

    return (
        <>
            {!showManualAnnotateModal && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Choose PDF photos"
                    style={backdropStyle}
                >
                    <div style={modalStyle}>
                        <div style={headerStyle}>
                            <div>
                                <h5
                                    style={{
                                        margin: 0,
                                        color: "#1f2937",
                                        fontSize: "18px",
                                        fontWeight: 750,
                                    }}
                                >
                                    Choose PDF Photos
                                </h5>

                                <div
                                    style={{
                                        marginTop: "3px",
                                        color: "#6b7280",
                                        fontSize: "12px",
                                    }}
                                >
                                    Door <strong>{currentDoor?.doorNumber}</strong>
                                    {" • "}

                                    <span style={{ color: "#dc2626", fontWeight: 700 }}>
                                        {selectedCount} / {PDF_PHOTO_SLOTS.length}
                                    </span>

                                    {" selected"}
                                </div>
                            </div>

                            <CircleCloseButton
                                onClick={onClose}
                                title="Close"
                                size={36}
                            />
                        </div>

                        <div style={bodyStyle}>
                            <div style={leftStyle}>
                                <div
                                    style={{
                                        color: "#1f2937",
                                        fontSize: "13px",
                                        fontWeight: 750,
                                    }}
                                >
                                    Final PDF Photos
                                </div>

                                <span
                                    style={{
                                        display: "block",
                                        margin: "3px 0 14px",
                                        color: "#8b8b8b",
                                        fontSize: "11px",
                                        lineHeight: 1.45,
                                    }}
                                >
                                    Select a slot, then choose any current or historical photo.
                                </span>

                                <div style={slotListStyle}>
                                    {PDF_PHOTO_SLOTS.map((slot) => {
                                        const url = selectedPhotos[slot.key];
                                        const active = activeSlot === slot.key;
                                        const hovered = hoveredSlot === slot.key;

                                        return (
                                            <div
                                                key={slot.key}
                                                role="button"
                                                tabIndex={0}
                                                onMouseEnter={() => setHoveredSlot(slot.key)}
                                                onMouseLeave={() => setHoveredSlot(null)}
                                                onClick={() => setActiveSlot(slot.key)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        event.preventDefault();
                                                        setActiveSlot(slot.key);
                                                    }
                                                }}
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "11px",
                                                    padding: "8px",
                                                    border: `2px solid ${active
                                                        ? "#18181b"
                                                        : hovered
                                                            ? "#d4d4d8"
                                                            : "transparent"
                                                        }`,
                                                    borderRadius: "12px",
                                                    background: active
                                                        ? "#f4f4f5"
                                                        : hovered
                                                            ? "#fafafa"
                                                            : "#fff",
                                                    cursor: "pointer",
                                                    transition:
                                                        "border-color 0.17s ease, background-color 0.17s ease",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: "74px",
                                                        height: "60px",
                                                        flex: "0 0 74px",
                                                        overflow: "hidden",
                                                        borderRadius: "9px",
                                                        background: "#f3f4f6",
                                                    }}
                                                >
                                                    {url ? (
                                                        <img
                                                            src={url}
                                                            alt={slot.label}
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                color: "#dc2626",
                                                                background: "#f4f4f5",
                                                                fontSize: "23px",
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faImage} />
                                                        </div>
                                                    )}
                                                </div>

                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div
                                                        style={{
                                                            color: "#27272a",
                                                            fontSize: "12px",
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {slot.label}
                                                    </div>

                                                    <div
                                                        style={{
                                                            marginTop: "2px",
                                                            color: url ? "#18181b" : "#9ca3af",
                                                            fontSize: "10px",
                                                            fontWeight: url ? 600 : 400,
                                                        }}
                                                    >
                                                        {url ? "Photo selected" : "No photo selected"}
                                                    </div>

                                                    {url &&
                                                        DESTINATION_ANNOTATION_FIELDS[
                                                        slot.field
                                                        ] && (
                                                            <button
                                                                type="button"
                                                                title={`Label ${slot.label}`}
                                                                disabled={savingAnnotation}
                                                                onClick={(event) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    void handleOpenAnnotateModal(slot);
                                                                }}
                                                                style={{
                                                                    marginTop: "6px",
                                                                    padding: "4px 8px",
                                                                    borderWidth: "1px",
                                                                    borderStyle: "solid",
                                                                    borderColor: "#d4d4d8",
                                                                    borderRadius: "7px",
                                                                    background: "#fff",
                                                                    color: "#18181b",
                                                                    fontSize: "9px",
                                                                    fontWeight: 700,
                                                                    cursor: savingAnnotation
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                    opacity: savingAnnotation
                                                                        ? 0.55
                                                                        : 1,
                                                                }}
                                                                onMouseEnter={(event) => {
                                                                    if (savingAnnotation) return;
                                                                    event.currentTarget.style.borderColor = "#dc2626";
                                                                    event.currentTarget.style.background = "#dc2626";
                                                                    event.currentTarget.style.color = "#fff";
                                                                }}
                                                                onMouseLeave={(event) => {
                                                                    event.currentTarget.style.borderColor = "#d4d4d8";
                                                                    event.currentTarget.style.background = "#fff";
                                                                    event.currentTarget.style.color = "#18181b";
                                                                }}
                                                            >
                                                                Label / Annotate
                                                            </button>
                                                        )}
                                                </div>

                                                {url && (
                                                    <CircleCloseButton
                                                        size={28}
                                                        title={`Remove ${slot.label}`}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            clearSlot(slot.key);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={rightStyle}>
                                <div style={{ marginBottom: "13px" }}>
                                    <div
                                        style={{
                                            color: "#303030",
                                            fontSize: "13px",
                                            fontWeight: 700,
                                        }}
                                    >
                                        Choose photo for{" "}
                                        <span style={{ color: "#dc2626" }}>
                                            {
                                                PDF_PHOTO_SLOTS.find(
                                                    (slot) => slot.key === activeSlot,
                                                )?.label
                                            }
                                        </span>
                                    </div>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "nowrap",
                                        gap: "7px",
                                        marginBottom: "13px",
                                        paddingTop: "4px",
                                        paddingBottom: "7px",
                                        overflowX: "auto",
                                        overflowY: "hidden",
                                    }}
                                >
                                    <HoverButton
                                        isActive={selectedDate === "all"}
                                        onClick={() => setSelectedDate("all")}
                                        style={{
                                            flex: "0 0 auto",
                                            padding: "6px 13px",
                                            border: "1px solid #d4d4d8",
                                            borderRadius: "999px",
                                            background: "#fff",
                                            color: "#27272a",
                                            fontSize: "11px",
                                            fontWeight: 650,
                                            cursor: "pointer",
                                            whiteSpace: "nowrap",
                                            transform: "scale(1)",
                                            transformOrigin: "top center",
                                            transition:
                                                "background-color 0.17s ease, border-color 0.17s ease, color 0.17s ease, transform 0.17s ease",
                                        }}
                                        activeStyle={{
                                            background: "#18181b",
                                            border: "1px solid #18181b",
                                            color: "#fff",
                                        }}
                                        hoverStyle={{
                                            background: selectedDate === "all" ? "#27272a" : "#f4f4f5",
                                            border:
                                                `1px solid ${selectedDate === "all" ? "#27272a" : "#dc2626"}`,
                                            color: selectedDate === "all" ? "#fff" : "#b91c1c",
                                            transform: "scale(1.03)",
                                        }}
                                    >
                                        All

                                        <span
                                            style={{
                                                marginLeft: "4px",
                                                opacity: 0.75,
                                                fontSize: "9px",
                                            }}
                                        >
                                            {photoCandidates.length}
                                        </span>
                                    </HoverButton>

                                    {dateTabs.map((date) => {
                                        const active = selectedDate === date.key;

                                        return (
                                            <HoverButton
                                                key={date.key}
                                                isActive={active}
                                                onClick={() => setSelectedDate(date.key)}
                                                style={{
                                                    flex: "0 0 auto",
                                                    padding: "6px 13px",
                                                    border: "1px solid #d4d4d8",
                                                    borderRadius: "999px",
                                                    background: "#fff",
                                                    color: "#27272a",
                                                    fontSize: "11px",
                                                    fontWeight: 650,
                                                    cursor: "pointer",
                                                    whiteSpace: "nowrap",
                                                    transform: "scale(1)",
                                                    transformOrigin: "top center",
                                                    transition:
                                                        "background-color 0.17s ease, border-color 0.17s ease, color 0.17s ease, transform 0.17s ease",
                                                }}
                                                activeStyle={{
                                                    background: "#18181b",
                                                    border: "1px solid #18181b",
                                                    color: "#fff",
                                                }}
                                                hoverStyle={{
                                                    background: active ? "#27272a" : "#f4f4f5",
                                                    border: `1px solid ${active ? "#27272a" : "#dc2626"}`,
                                                    color: active ? "#fff" : "#b91c1c",
                                                    transform: "scale(1.03)",
                                                }}
                                            >
                                                {date.label}

                                                {date.hasCurrent}

                                                <span
                                                    style={{
                                                        marginLeft: "4px",
                                                        opacity: 0.75,
                                                        fontSize: "9px",
                                                    }}
                                                >
                                                    {date.count}
                                                </span>
                                            </HoverButton>
                                        );
                                    })}
                                </div>

                                <div
                                    style={{
                                        minHeight: 0,
                                        flex: 1,
                                        overflowY: isTablet ? "visible" : "auto",
                                        paddingRight: "3px",
                                    }}
                                >
                                    {selectedDate === "all" ? (
                                        dateGroups.length > 0 ? (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "27px",
                                                }}
                                            >
                                                {dateGroups.map((group) => (
                                                    <div key={group.key}>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: "9px",
                                                                marginBottom: "12px",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    flex: 1,
                                                                    height: "1px",
                                                                    background:
                                                                        "linear-gradient(90deg, #e5e7eb, #18181b, #e5e7eb)",
                                                                }}
                                                            />

                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "6px",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        padding: "5px 10px",
                                                                        border: "1px solid #d4d4d8",
                                                                        borderRadius: "999px",
                                                                        background: "#f4f4f5",
                                                                        color: "#18181b",
                                                                        fontSize: "11px",
                                                                        fontWeight: 750,
                                                                    }}
                                                                >
                                                                    {group.dateLabel}
                                                                </span>

                                                                {!isMobile && (
                                                                    <span
                                                                        style={{
                                                                            color: "#a1a1aa",
                                                                            fontSize: "9px",
                                                                            fontWeight: 600,
                                                                        }}
                                                                    >
                                                                        {group.photos.length}{" "}
                                                                        {group.photos.length === 1 ? "photo" : "photos"}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div
                                                                style={{
                                                                    flex: 1,
                                                                    height: "1px",
                                                                    background:
                                                                        "linear-gradient(90deg, #e5e7eb, #18181b, #e5e7eb)",
                                                                }}
                                                            />
                                                        </div>

                                                        <div style={photoGridStyle}>
                                                            {group.photos.map(renderPhotoCard)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            renderEmptyState(
                                                "No inspection or work-done photos were found.",
                                            )
                                        )
                                    ) : visiblePhotos.length > 0 ? (
                                        <div style={photoGridStyle}>
                                            {visiblePhotos.map(renderPhotoCard)}
                                        </div>
                                    ) : (
                                        renderEmptyState(
                                            "No photos are available for this inspection.",
                                        )
                                    )}
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                minHeight: "68px",
                                display: "flex",
                                alignItems: "center",
                                flexWrap: isMobile ? "wrap" : "nowrap",
                                gap: "9px",
                                padding: isMobile ? "10px 12px" : "12px 18px",
                                borderTop: "1px solid #e5e7eb",
                                background: "#fff",
                            }}
                        >
                            <div
                                style={{
                                    width: isMobile ? "100%" : "auto",
                                    marginRight: isMobile ? 0 : "auto",
                                }}
                            >
                                <HoverButton
                                    disabled={generating}
                                    onClick={resetPhotos}
                                    style={{
                                        width: isMobile ? "100%" : "auto",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "7px",
                                        padding: "8px 14px",
                                        border: "1px solid #27272a",
                                        borderRadius: "9px",
                                        background: "#fff",
                                        color: "#18181b",
                                        fontSize: "11px",
                                        fontWeight: 650,
                                        cursor: generating ? "not-allowed" : "pointer",
                                        opacity: generating ? 0.58 : 1,
                                        transition: "all 0.17s ease",
                                    }}
                                    hoverStyle={{
                                        background: "#f4f4f5",
                                        border: "1px solid #f87171",
                                        color: "#991b1b",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faRotateLeft} />
                                    Reset to Current
                                </HoverButton>
                            </div>

                            <HoverButton
                                disabled={generating}
                                onClick={onClose}
                                style={{
                                    flex: isMobile ? 1 : "none",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "7px",
                                    padding: "8px 14px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "9px",
                                    background: "#fff",
                                    color: "#52525b",
                                    fontSize: "11px",
                                    fontWeight: 650,
                                    cursor: generating ? "not-allowed" : "pointer",
                                    opacity: generating ? 0.58 : 1,
                                    transition: "all 0.17s ease",
                                }}
                                hoverStyle={{
                                    border: "1px solid #fecaca",
                                    background: "#f4f4f5",
                                    color: "#b91c1c",
                                }}
                            >
                                Cancel
                            </HoverButton>

                            <HoverButton
                                disabled={generating}
                                onClick={handleGenerate}
                                style={{
                                    flex: isMobile ? 1 : "none",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "7px",
                                    padding: "8px 14px",
                                    border: "1px solid #dc2626",
                                    borderRadius: "9px",
                                    background: "#dc2626",
                                    color: "#fff",
                                    fontSize: "11px",
                                    fontWeight: 650,
                                    cursor: generating ? "not-allowed" : "pointer",
                                    opacity: generating ? 0.58 : 1,
                                    transition:
                                        "background-color 0.17s ease, border-color 0.17s ease",
                                }}
                                hoverStyle={{
                                    background: "#b91c1c",
                                    border: "1px solid #b91c1c",
                                }}
                            >
                                <FontAwesomeIcon icon={faDownload} />
                                {generating ? "Generating..." : `Generate PDF (${selectedCount})`}
                            </HoverButton>
                        </div>
                    </div>
                </div>
            )}

            {showManualAnnotateModal &&
                loadingAnnotation && (
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Loading annotations"
                        style={backdropStyle}
                    >
                        <div
                            style={{
                                width:
                                    isMobile
                                        ? "calc(100% - 24px)"
                                        : "min(560px, calc(100% - 36px))",
                                overflow: "hidden",
                                borderRadius: "16px",
                                background: "#fff",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    padding: "16px 18px",
                                    borderBottom:
                                        "1px solid #e5e7eb",
                                }}
                            >
                                <div
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#18181b",
                                            fontSize: "16px",
                                            fontWeight: 750,
                                        }}
                                    >
                                        Loading annotations...
                                    </div>
                                </div>

                                <CircleCloseButton
                                    onClick={() => {
                                        annotationLoadRequestRef.current += 1;
                                        setShowManualAnnotateModal(false);
                                        setCurrentAnnotatingDoor(null);
                                        setShowAnnotateModalResult(null);
                                        setAnnotationLoadError(null);
                                        setAnnotationLoadingInfo(null);
                                        setLoadingAnnotation(false);
                                    }}
                                    title="Cancel annotation load"
                                    size={34}
                                />
                            </div>

                            <div
                                style={{
                                    display:
                                        isMobile
                                            ? "block"
                                            : "grid",
                                    gridTemplateColumns:
                                        "150px 1fr",
                                    gap: "16px",
                                    padding: "18px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        height: "150px",
                                        overflow: "hidden",
                                        borderRadius: "11px",
                                        background: "#f4f4f5",
                                    }}
                                >
                                    {annotationLoadingInfo?.imageSrc && (
                                        <img
                                            src={
                                                annotationLoadingInfo.imageSrc
                                            }
                                            alt={
                                                annotationLoadingInfo.imageName ||
                                                "Selected photo"
                                            }
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />
                                    )}
                                </div>

                                <div
                                    style={{
                                        minWidth: 0,
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#27272a",
                                            fontSize: "13px",
                                            fontWeight: 750,
                                        }}
                                    >
                                        {
                                            annotationLoadingInfo?.imageName
                                        }
                                    </div>

                                    <div
                                        style={{
                                            marginTop: "8px",
                                            color: "#52525b",
                                            fontSize: "11px",
                                            lineHeight: 1.55,
                                        }}
                                    >
                                        {
                                            annotationLoadingInfo?.sourceLabel ||
                                            "Selected photo"
                                        }
                                    </div>

                                    <div
                                        style={{
                                            marginTop: "14px",
                                            height: "6px",
                                            overflow: "hidden",
                                            borderRadius: "999px",
                                            background: "#e5e7eb",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: "55%",
                                                height: "100%",
                                                borderRadius: "999px",
                                                background: "#dc2626",
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            {showManualAnnotateModal &&
                !loadingAnnotation &&
                annotationLoadError && (
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Annotation loading error"
                        style={backdropStyle}
                    >
                        <div
                            style={{
                                width:
                                    isMobile
                                        ? "calc(100% - 24px)"
                                        : "min(520px, calc(100% - 36px))",
                                padding: "20px",
                                borderRadius: "16px",
                                background: "#fff",
                            }}
                        >
                            <div
                                style={{
                                    color: "#991b1b",
                                    fontSize: "16px",
                                    fontWeight: 750,
                                }}
                            >
                                Failed to load annotations
                            </div>

                            <div
                                style={{
                                    marginTop: "8px",
                                    color: "#52525b",
                                    fontSize: "12px",
                                    lineHeight: 1.55,
                                }}
                            >
                                {annotationLoadError}
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    marginTop: "16px",
                                }}
                            >
                                <HoverButton
                                    onClick={() => {
                                        annotationLoadRequestRef.current += 1;
                                        setShowManualAnnotateModal(false);
                                        setCurrentAnnotatingDoor(null);
                                        setShowAnnotateModalResult(null);
                                        setAnnotationLoadError(null);
                                        setAnnotationLoadingInfo(null);
                                        setLoadingAnnotation(false);
                                    }}
                                    style={{
                                        padding: "8px 14px",
                                        border: "1px solid #d4d4d8",
                                        borderRadius: "8px",
                                        background: "#fff",
                                        color: "#18181b",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        cursor: "pointer",
                                    }}
                                    hoverStyle={{
                                        border: "1px solid #dc2626",
                                        color: "#b91c1c",
                                    }}
                                >
                                    Close
                                </HoverButton>
                            </div>
                        </div>
                    </div>
                )}

            {showManualAnnotateModal &&
                !loadingAnnotation &&
                !annotationLoadError &&
                showAnnotateModalResult && (
                    <AnnotateModal
                        show={true}
                        imageSrc={
                            showAnnotateModalResult?.imageSrc
                        }
                        regionClsList={
                            showAnnotateModalResult?.regionClsList ||
                            []
                        }
                        regions={
                            showAnnotateModalResult?.regions ||
                            []
                        }
                        imageName={
                            showAnnotateModalResult?.imageName
                        }
                        onClose={() => {
                            annotationLoadRequestRef.current += 1;
                            setShowManualAnnotateModal(false);
                            setCurrentAnnotatingDoor(null);
                            setShowAnnotateModalResult(null);
                            setAnnotationLoadError(null);
                            setAnnotationLoadingInfo(null);
                            setLoadingAnnotation(false);
                        }}
                        onSave={({ regions }) => {
                            if (
                                !showAnnotateModalResult ||
                                !currentAnnotatingDoor
                            ) {
                                return;
                            }

                            saveManualAnnotation({
                                regions,
                            });
                        }}
                    />
                )}
        </>
    );
};

export default PhotoSelector;